import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Boxes, Loader2, Sparkles, TrendingDown } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";

function fmtCHF(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} CHF`;
}

/**
 * Bouton + dialog pour insérer un pack B2B dans un formulaire de commande client.
 *
 * Props :
 * - onInsert(lines): callback. `lines` est un tableau de lignes prêtes à être
 *   ajoutées au tableau form.lines (chaque ligne a product_id, quantity,
 *   unit_price, vat_rate, notes, pack_id, pack_label).
 * - defaultVatRate (number, default 2.6) : utilisé seulement si le pack
 *   n'a pas de vat_rate.
 */
export default function PackInsertDialog({ onInsert, defaultVatRate = 2.6, buttonLabel = "Insérer un pack", testIdPrefix = "pack-insert" }) {
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [preview, setPreview] = useState(null);
  const [inserting, setInserting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/packs");
        if (alive) setPacks(data || []);
      } catch (e) {
        if (alive) toast.error("Impossible de charger les packs");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!selectedId) { setPreview(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/packs/${selectedId}/expanded`);
        if (alive) setPreview(data);
      } catch (e) {
        if (alive) {
          toast.error("Impossible de charger l'aperçu du pack");
          setPreview(null);
        }
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  const handleInsert = () => {
    if (!preview) return;
    const vat = Number(preview.vat_rate) || defaultVatRate;
    const lines = (preview.components_expanded || []).map((c) => ({
      product_id: c.product_id,
      product_name: c.product_name,
      product_sku: c.product_sku,
      quantity: c.quantity,
      unit: c.unit || "pcs",
      unit_price: c.unit_price,
      vat_rate: vat,
      notes: "",
      unavailable: false,
      pack_id: preview.id,
      pack_label: preview.name,
    }));
    setInserting(true);
    try {
      onInsert?.(lines);
      toast.success(`Pack « ${preview.name} » inséré (${lines.length} ligne${lines.length > 1 ? "s" : ""})`);
      setOpen(false);
      setSelectedId("");
      setPreview(null);
    } finally {
      setInserting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSelectedId(""); setPreview(null); } }}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-[#1F4E3D]/30 text-[#1F4E3D] hover:bg-[#EAF4EE]"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <Boxes className="w-3.5 h-3.5 mr-1.5" /> {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Boxes className="w-5 h-5 text-[#1F4E3D]" />
            Insérer un pack prêt-à-vendre
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un pack B2B : ses composants seront ajoutés en tant que lignes individuelles avec un prix unitaire calculé pour respecter le prix total du pack.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="mb-1.5 block">Pack</Label>
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-[#575A57] p-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement des packs…
              </div>
            ) : packs.length === 0 ? (
              <div className="text-xs text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded p-4 text-center">
                Aucun pack défini. Créez-en un dans <span className="font-mono">/packs</span>.
              </div>
            ) : (
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid={`${testIdPrefix}-select`}>
                  <SelectValue placeholder="Choisir un pack…" />
                </SelectTrigger>
                <SelectContent>
                  {packs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {fmtCHF(p.pack_price_chf)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {preview && (
            <div className="space-y-3" data-testid={`${testIdPrefix}-preview`}>
              {preview.description && (
                <div className="text-xs text-[#575A57] italic border-l-2 border-[#1F4E3D]/30 pl-3">
                  {preview.description}
                </div>
              )}

              <div className="border border-[#E5E5E0] rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F4F0]">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-[#575A57]">Produit</th>
                      <th className="text-right px-3 py-2 font-semibold text-[#575A57]">Qté</th>
                      <th className="text-right px-3 py-2 font-semibold text-[#575A57]">PU CHF</th>
                      <th className="text-right px-3 py-2 font-semibold text-[#575A57]">Total ligne</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.components_expanded || []).map((c, i) => (
                      <tr key={i} className="border-t border-[#E5E5E0]">
                        <td className="px-3 py-1.5">
                          <div className="font-medium text-[#1A1C1A]">{c.product_name}</div>
                          <div className="text-[10px] text-[#A3A6A3] font-mono">{c.product_sku}</div>
                        </td>
                        <td className="text-right px-3 py-1.5 font-mono">{c.quantity}</td>
                        <td className="text-right px-3 py-1.5 font-mono">{Number(c.unit_price).toFixed(2)}</td>
                        <td className="text-right px-3 py-1.5 font-mono font-semibold">{Number(c.line_total).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#EAF4EE] border-t border-[#C5DBCF]">
                      <td colSpan={3} className="text-right px-3 py-2 font-semibold text-[#1F4E3D]">Total pack TTC</td>
                      <td className="text-right px-3 py-2 font-mono font-bold text-[#1F4E3D]">{fmtCHF(preview.pack_price_chf)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex items-center gap-3 flex-wrap text-xs">
                <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]">
                  Référence B2B : {fmtCHF(preview.reference_total_chf)}
                </Badge>
                {preview.savings_chf > 0 && (
                  <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]">
                    <TrendingDown className="w-3 h-3 mr-1" />
                    Économie {fmtCHF(preview.savings_chf)} ({preview.savings_pct}%)
                  </Badge>
                )}
                <Badge variant="outline">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {preview.total_units} unité{preview.total_units > 1 ? "s" : ""}
                </Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button
            onClick={handleInsert}
            disabled={!preview || inserting}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid={`${testIdPrefix}-confirm`}
          >
            {inserting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Insérer le pack
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
